import {SignalType} from 'gabi-api-ts/v2/common/signals/signals';
import {Patient} from 'gabi-api-ts/v2/patient/query/patient_query';
import {SignalQueryServiceClient} from 'gabi-api-ts/v2/signal/query/signal_query.client';

import {HealthReportSpotSignal} from '@/components/business/analytics/health-report/health-report-spot-signal';
import {NoDataCell} from '@/components/business/analytics/patient-overview-cells/no-data-cell';
import {PatientsActiveTableCellHoverable} from '@/components/business/analytics/patients/patients-active-table-cell-hoverable';
import ErrorBoundary from '@/components/errors/error-boundary';
import ColoredContainer from '@/components/layout/colored-container';
import {MiniLoadingView} from '@/components/static/mini-loading-view';
import {useBackendQuery} from '@/hooks/use-backend-query';
import {SignalDefinitions} from '@/model/signal-definitions/signal-definition';
import {addDays} from '@/util/apidate-util';
import {getSignalTypeUnit} from '@/util/signal-type-util';

type SpotCellProps = {
    patient: Patient;
    signalType: SignalType;
    handleWidgetChartClick?: (selectedDate: Date) => void;
}

function SpotCell({patient, signalType}: SpotCellProps) {
    const [latestPointLoading, latestPoint] = useBackendQuery({
        serviceClient: SignalQueryServiceClient,
        query: SignalQueryServiceClient.prototype.getLatestBiometricValues,
        memoize: true,
        data: {
            signalType: [signalType],
            patientId: patient.patientId,
        },
    });

    const signalColors = (SignalDefinitions[signalType] ?? SignalDefinitions[-1]).colors;

    if (latestPointLoading || !latestPoint) {
        return (
            <PatientsActiveTableCellHoverable
                hasHover={false}
                hoverElement={() => null}
            >
                <div className="text-center">
                    <MiniLoadingView />
                </div>
            </PatientsActiveTableCellHoverable>
        );
    }
    else {
        return (
            <PatientsActiveTableCellHoverable
                hasHover={true}
                hoverElement={() => (
                    <div className={'warning-overlay animate__animated animate__fadeIn animate__faster'}>
                        <ErrorBoundary>
                            <ColoredContainer color={signalColors.normal}>
                                <HealthReportSpotSignal
                                    patientId={patient.patientId?.id ?? ''}
                                    signalType={signalType}
                                    dateFrom={patient.lastRecordingDataTime!.date!}
                                    dateTo={addDays(patient.lastRecordingDataTime!.date!, 1)}
                                />
                            </ColoredContainer>
                        </ErrorBoundary>
                    </div>
                )}
            >
                {(latestPoint.values.length===0 || !latestPoint.values[0].hasData) ? (
                    <NoDataCell/>
                ) : (<>
                    <strong>{Math.round(latestPoint.values[0].value ?? 0)}</strong>
                    {' '}
                    {getSignalTypeUnit(signalType)}
                </>)}
            </PatientsActiveTableCellHoverable>
        );
    }
}

export {SpotCell};
